import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Rss, Download, ExternalLink, Loader2 } from "lucide-react";
import { NAV_CATEGORIES } from "../../mock";
import {
  adminListFeeds,
  adminCreateFeed,
  adminUpdateFeed,
  adminDeleteFeed,
  adminImportFeeds,
  adminImportStatus,
} from "../../api";

const empty = { id: null, name: "", url: "", category: "", active: true };

const FeedManager = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [msg, setMsg] = useState("");
  const [result, setResult] = useState(null);

  const load = async () => {
    try {
      setItems(await adminListFeeds());
    } catch (e) {
      setItems([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    const body = { name: form.name, url: form.url, category: form.category, active: form.active };
    try {
      if (form.id) await adminUpdateFeed(form.id, body);
      else await adminCreateFeed(body);
      setForm(null);
      await load();
    } catch (err) {
      setMsg("Errore nel salvataggio del feed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Eliminare il feed "${f.name}"?`)) return;
    await adminDeleteFeed(f.id);
    await load();
  };

  const runImport = async () => {
    setImporting(true);
    setResult(null);
    setMsg("");
    try {
      const r = await adminImportFeeds();
      if (r.running === false && r.started === false) {
        setMsg("Un'importazione è già in corso. Attendi il completamento.");
      }
      // Poll status until finished
      const poll = setInterval(async () => {
        try {
          const s = await adminImportStatus();
          if (!s.running) {
            clearInterval(poll);
            setImporting(false);
            if (s.error) {
              setMsg("Errore durante l'importazione (verifica il saldo della LLM key).");
            } else {
              setResult({ imported: s.imported || 0, feeds: s.feeds || 0 });
            }
            await load();
          }
        } catch (e) {
          clearInterval(poll);
          setImporting(false);
        }
      }, 5000);
    } catch (err) {
      setMsg("Errore durante l'avvio dell'importazione");
      setImporting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <h2 className="font-serif-display text-2xl text-sq-navy">Feed RSS</h2>
          <p className="text-sm text-gray-500">{items.length} fonti · importazione automatica ogni 12 ore</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={runImport}
            disabled={importing}
            className="flex items-center gap-2 bg-sq-navy hover:bg-sq-orange transition-colors text-white px-4 py-2.5 rounded-sm text-sm font-medium disabled:opacity-60"
          >
            {importing ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {importing ? "Importazione..." : "Importa ora"}
          </button>
          {!form && (
            <button
              onClick={() => setForm({ ...empty })}
              className="flex items-center gap-2 bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-4 py-2.5 rounded-sm text-sm font-medium"
            >
              <Plus size={16} /> Nuovo feed
            </button>
          )}
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-5 bg-sq-light border border-sq rounded-sm px-3 py-2">
        Gli articoli vengono <strong>rielaborati con l'AI in contenuti originali</strong> (no copia
        delle fonti), categorizzati automaticamente e <strong>pubblicati subito</strong>. Puoi
        eliminarli dalla scheda "Articoli".
      </p>

      {msg && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2">{msg}</div>}

      {importing && (
        <div className="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-700 rounded-sm px-3 py-2 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Importazione in corso in background (rielaborazione con AI)... può richiedere ~1 minuto.
        </div>
      )}

      {result && (
        <div className="mb-4 text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-sm px-3 py-2">
          Importati <strong>{result.imported}</strong> nuovi articoli da {result.feeds} feed.
          {result.imported === 0 && " (Nessuna nuova notizia trovata o tutte già importate.)"}
        </div>
      )}

      {form && (
        <form onSubmit={save} className="bg-white border border-sq rounded-md p-6 mb-8">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-medium text-sq-navy">{form.id ? "Modifica feed" : "Nuovo feed RSS"}</h3>
            <button type="button" onClick={() => setForm(null)} className="text-gray-400 hover:text-sq-navy">
              <X size={20} />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-sq-navy mb-1">Nome fonte</label>
              <input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                placeholder="Es. ANSA Economia"
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-sq-navy mb-1">Categoria predefinita (opzionale)</label>
              <select
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none bg-white"
              >
                <option value="">Automatica (decide l'AI)</option>
                {NAV_CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-sq-navy mb-1">URL del feed RSS</label>
              <input
                value={form.url}
                onChange={(e) => set("url", e.target.value)}
                required
                placeholder="https://www.esempio.it/rss/economia.xml"
                className="w-full border border-sq rounded-sm px-3 py-2.5 text-sm outline-none focus:border-sq-orange"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-sq-navy cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set("active", e.target.checked)}
                  className="w-4 h-4 accent-orange-500"
                />
                Attivo (incluso nell'importazione)
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              disabled={busy}
              className="bg-sq-orange hover:bg-sq-orange-dark transition-colors text-white px-5 py-2.5 rounded-sm text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Salvataggio..." : "Salva feed"}
            </button>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="border border-sq text-sq-navy px-5 py-2.5 rounded-sm text-sm hover:bg-sq-light"
            >
              Annulla
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-sq rounded-md overflow-hidden">
        <div className="divide-y divide-[#e6e8ec]">
          {items.map((f) => (
            <div key={f.id} className="flex items-center gap-4 px-4 py-3 hover:bg-sq-light">
              <div className="w-9 h-9 rounded-sm bg-sq-light border border-sq flex items-center justify-center flex-shrink-0">
                <Rss size={16} className="text-sq-orange" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sq-navy">{f.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${f.active ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>
                    {f.active ? "Attivo" : "Disattivo"}
                  </span>
                  {f.category && <span className="text-[10px] bg-sq-light border border-sq px-1.5 rounded">{f.category}</span>}
                </div>
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 flex items-center gap-1 truncate hover:text-sq-orange">
                  <ExternalLink size={11} /> {f.url}
                </a>
              </div>
              <button onClick={() => setForm({ id: f.id, name: f.name, url: f.url, category: f.category || "", active: f.active })} className="text-gray-400 hover:text-sq-orange p-2">
                <Pencil size={16} />
              </button>
              <button onClick={() => remove(f)} className="text-gray-400 hover:text-red-600 p-2">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="px-4 py-8 text-center text-gray-500 text-sm">Nessun feed configurato.</div>}
        </div>
      </div>
    </div>
  );
};

export default FeedManager;
